import { memo, useCallback, useId, useRef, useState } from 'react';
import {
  AddCircleOutline as AddCircleOutlineIcon,
  DragIndicator as DragIndicatorIcon,
  MoreHoriz as MoreHorizIcon,
} from '@mui/icons-material';
import { IconButton, InputBase, MenuItem, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { BlockWidget, SectionConfig, WidgetConfig } from '@trinity/chat/types/dashboard-config';
import {
  useDashboardBuilder,
  useDashboardBuilderEditing,
  useWidgetRegistry,
} from '@trinity/chat-components/containers/Dashboard/context';
import { useDashboardData } from '@trinity/chat-components/containers/Dashboard/context';
import { ROW_HEIGHT, getSectionEffectiveRows } from './dashboardLayoutUtils';
import { WidgetPalette } from './WidgetPalette';
import { ThemedMenu } from './shared-styled';

// ─── Types ────────────────────────────────────────────────────────────────────

type Slot = 1 | 2 | 3;

type Props = {
  blockId: string;
  section: SectionConfig;
  /** Column span of this section in the parent block — drives the inner grid column count */
  sectionItemSpan: number;
  /** Row of this section in the parent block (used for drag data) */
  sectionItemRow: number;
  /** Slot of this section in the parent block (used for drag data) */
  sectionItemSlot: Slot;
  /** When true, renders in clean read-only preview mode (no editing chrome). */
  readOnly?: boolean;
};

type SlotItem = {
  slot: Slot;
  bw: BlockWidget | null;
  isPrimaryEmpty: boolean;
};

type SectionDragData = {
  kind: 'section-move';
  blockId: string;
  row: number;
  slot: Slot;
};

type WidgetDragData =
  | { kind: 'section-widget-move'; blockId: string; sectionId: string; row: number; slot: Slot }
  | { kind: 'section-widget-resize'; blockId: string; sectionId: string; row: number; slot: Slot }
  /** Widget dragged from a parent block slot — enables block→section drops */
  | { kind: 'move'; blockId: string; row: number; slot: Slot };

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getMaxRow(widgets: BlockWidget[]): number {
  if (widgets.length === 0) return 1;
  return Math.max(...widgets.map(w => w.row ?? 1));
}

function isRowFull(widgets: BlockWidget[], row: number, maxCols: number): boolean {
  const rowWidgets = widgets.filter(w => (w.row ?? 1) === row);
  return rowWidgets.reduce((sum, bw) => sum + Math.min(bw.span, maxCols - bw.slot + 1), 0) >= maxCols;
}

function computeRowSlots(widgets: BlockWidget[], row: number, maxCols: number): SlotItem[] {
  const rowWidgets = widgets.filter(w => (w.row ?? 1) === row && w.slot <= maxCols);
  const items: SlotItem[] = [];
  let pos = 1;
  let primaryEmptyAssigned = false;
  while (pos <= maxCols) {
    const bw = rowWidgets.find(w => w.slot === pos);
    if (bw) {
      // Clamp span to the remaining columns in this row
      const effectiveSpan = Math.min(bw.span, maxCols - pos + 1);
      items.push({ slot: pos as Slot, bw: { ...bw, span: effectiveSpan as Slot }, isPrimaryEmpty: false });
      pos += effectiveSpan;
    } else {
      const isPrimaryEmpty = !primaryEmptyAssigned;
      if (isPrimaryEmpty) primaryEmptyAssigned = true;
      items.push({ slot: pos as Slot, bw: null, isPrimaryEmpty });
      pos += 1;
    }
  }
  return items;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * SectionCardShell — a bordered sub-block shell that lives inside a parent block.
 *
 * Visually it is a "card within a card": a tinted, bordered panel with its own
 * title bar and an independent 3-column slot grid where widgets can be placed,
 * dragged, and resized just like in the parent WidgetCardShell.
 *
 * The whole section can be dragged to a different (row, slot) in the parent block
 * via the drag handle in its title bar.
 */
export const SectionCardShell = memo(function SectionCardShell({
  blockId,
  section,
  sectionItemSpan,
  sectionItemRow,
  sectionItemSlot,
  readOnly = false,
}: Props): React.ReactElement {
  const {
    removeSection,
    updateSectionTitle,
    toggleSectionHideTitle,
    assignWidgetToSection,
    removeWidgetFromSection,
    moveWidgetInSection,
    moveWidgetFromBlockToSection,
    moveWidgetBetweenSections,
    updateSectionWidgetSpan,
    updateSectionMinRows,
  } = useDashboardBuilderEditing();
  const { setSelectedWidgetId, setSidebarPanel, setSelectedBlockId } = useDashboardBuilder();

  const widgets = section.widgets;
  const maxCols = Math.max(1, Math.min(3, sectionItemSpan));
  const hideTitle = section.hideTitle ?? false;
  // Header height depends on show/hide mode (matches block header dual-mode)
  const SECTION_COMPACT_HEADER_H = 24;
  const SECTION_FULL_HEADER_H = 44;
  // In readOnly preview, the header is in normal flow (no absolute overlay), so no top padding needed.
  const sectionHeaderH = readOnly ? 0 : (hideTitle ? SECTION_COMPACT_HEADER_H : SECTION_FULL_HEADER_H);

  const derivedMaxRow = getMaxRow(widgets);
  const rowFull = isRowFull(widgets, derivedMaxRow, maxCols);
  const derivedRowCount = rowFull ? derivedMaxRow + 1 : derivedMaxRow;
  // In preview/readOnly mode: only render rows with actual widget content — no trailing
  // empty row for drop targets and no minInnerRows padding.
  const rowCount = readOnly ? derivedMaxRow : Math.max(section.minInnerRows ?? 1, derivedRowCount);
  const rows = Array.from({ length: rowCount }, (_, i) => i + 1);

  // ── Section vertical drag-resize ────────────────────────────────────────
  // Mouse-based pointer capture so the drag escapes the section boundary
  const resizeStartRef = useRef<{ y: number; rows: number } | null>(null);

  const handleResizePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.setPointerCapture(e.pointerId);
      resizeStartRef.current = { y: e.clientY, rows: rowCount };
    },
    [rowCount],
  );

  const handleResizePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!resizeStartRef.current) return;
      const deltaY = e.clientY - resizeStartRef.current.y;
      const deltaRows = Math.round(deltaY / ROW_HEIGHT);
      // Content floor: the minimum inner rows derived purely from widget content
      const contentFloor = getSectionEffectiveRows({ widgets, minInnerRows: undefined }, maxCols);
      const newRows = Math.max(contentFloor, resizeStartRef.current.rows + deltaRows);
      if (newRows !== (section.minInnerRows ?? rowCount)) {
        updateSectionMinRows(blockId, section.id, newRows);
      }
    },
    [blockId, section.id, section.minInnerRows, rowCount, widgets, maxCols, updateSectionMinRows],
  );

  const handleResizePointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!resizeStartRef.current) return;
      const deltaY = e.clientY - resizeStartRef.current.y;
      const deltaRows = Math.round(deltaY / ROW_HEIGHT);
      const contentFloor = getSectionEffectiveRows({ widgets, minInnerRows: undefined }, maxCols);
      const newRows = Math.max(contentFloor, resizeStartRef.current.rows + deltaRows);
      updateSectionMinRows(blockId, section.id, newRows);
      resizeStartRef.current = null;
      e.currentTarget.releasePointerCapture(e.pointerId);
    },
    [blockId, section.id, widgets, maxCols, updateSectionMinRows],
  );

  // ── Title ──────────────────────────────────────────────────────────────────
  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => updateSectionTitle(blockId, section.id, e.target.value),
    [updateSectionTitle, blockId, section.id],
  );

  // ── Section menu ───────────────────────────────────────────────────────────
  const sectionMenuId = useId();
  const [sectionMenuAnchor, setSectionMenuAnchor] = useState<null | HTMLElement>(null);

  // ── Per-widget menu ────────────────────────────────────────────────────────
  const widgetMenuId = useId();
  const [widgetMenu, setWidgetMenu] = useState<{ anchorEl: HTMLElement; row: number; slot: Slot } | null>(null);
  const widgetMenuBw = widgetMenu
    ? (widgets.find(bw => bw.slot === widgetMenu.slot && (bw.row ?? 1) === widgetMenu.row) ?? null)
    : null;

  // ── Palette ────────────────────────────────────────────────────────────────
  const [paletteTarget, setPaletteTarget] = useState<{ row: number; slot: Slot } | null>(null);

  // ── Widget drag & drop (within section) ───────────────────────────────────
  const [dragOverTarget, setDragOverTarget] = useState<{ row: number; slot: Slot } | null>(null);

  const handleWidgetDragStart = useCallback(
    (e: React.DragEvent, row: number, slot: Slot) => {
      const data: WidgetDragData = { kind: 'section-widget-move', blockId, sectionId: section.id, row, slot };
      e.dataTransfer.setData('text/plain', JSON.stringify(data));
      e.dataTransfer.effectAllowed = 'move';
      e.stopPropagation();
    },
    [blockId, section.id],
  );

  const handleResizeDragStart = useCallback(
    (e: React.DragEvent, row: number, slot: Slot) => {
      e.stopPropagation();
      const data: WidgetDragData = { kind: 'section-widget-resize', blockId, sectionId: section.id, row, slot };
      e.dataTransfer.setData('text/plain', JSON.stringify(data));
      e.dataTransfer.effectAllowed = 'move';
    },
    [blockId, section.id],
  );

  const handleDragOver = useCallback((e: React.DragEvent, row: number, slot: Slot) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragOverTarget({ row, slot });
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, toRow: number, toSlot: Slot) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOverTarget(null);
      try {
        const raw = e.dataTransfer.getData('text/plain');
        if (!raw) return;
        const data = JSON.parse(raw) as WidgetDragData;
        if (data.kind === 'section-widget-move') {
          if (data.sectionId === section.id) {
            // Same section — reorder within
            moveWidgetInSection(blockId, section.id, data.row, data.slot, toRow, toSlot);
          } else {
            // Different section (same or different block) — cross-section move
            moveWidgetBetweenSections(data.blockId, data.sectionId, data.row, data.slot, blockId, section.id, toRow, toSlot);
          }
        } else if (data.kind === 'move') {
          // Widget dragged from a parent block slot into this section
          moveWidgetFromBlockToSection(data.blockId, data.row, data.slot, blockId, section.id, toRow, toSlot);
        } else if (data.kind === 'section-widget-resize' && data.sectionId === section.id) {
          if (data.row === toRow && toSlot >= data.slot) {
            const newSpan = Math.min(maxCols, Math.max(1, toSlot - data.slot + 1)) as Slot;
            updateSectionWidgetSpan(blockId, section.id, data.row, data.slot, newSpan);
          }
        }
      } catch { /* ignore malformed drag data */ }
    },
    [blockId, section.id, moveWidgetInSection, moveWidgetBetweenSections, moveWidgetFromBlockToSection, updateSectionWidgetSpan, maxCols],
  );

  // ── Section drag (for moving section in parent block) ─────────────────────
  const handleSectionDragStart = useCallback(
    (e: React.DragEvent) => {
      const data: SectionDragData = { kind: 'section-move', blockId, row: sectionItemRow, slot: sectionItemSlot };
      e.dataTransfer.setData('text/plain', JSON.stringify(data));
      e.dataTransfer.effectAllowed = 'move';
    },
    [blockId, sectionItemRow, sectionItemSlot],
  );

  const handleConfigure = useCallback(
    (row: number, slot: Slot) => {
      const bw = widgets.find(w => w.slot === slot && (w.row ?? 1) === row);
      if (!bw) return;
      setSelectedBlockId(blockId);
      setSelectedWidgetId(bw.widget.id);
      setSidebarPanel('chart-config');
      setWidgetMenu(null);
    },
    [widgets, blockId, setSelectedBlockId, setSelectedWidgetId, setSidebarPanel],
  );

  // Track whether the section-level primary "Add Widget" placeholder has been assigned.
  // Mutable variable before return — safe because rows.map runs synchronously in render.
  let sectionPrimaryAssigned = false;

  return (
    <SectionShell>
      {/* ─── Section header ─── */}
      {readOnly ? (
        // Preview: clean title-only header, hidden when hideTitle=true or there is no title
        (!hideTitle && section.title) ? (
          <SectionPreviewTitle variant="subtitle1" fontWeight={700} noWrap>
            {section.title}
          </SectionPreviewTitle>
        ) : null
      ) : (
        <SectionHeader hideTitle={hideTitle} draggable onDragStart={handleSectionDragStart}>
          <DragIndicatorIcon sx={{ fontSize: 16, color: 'text.disabled', cursor: 'grab', flexShrink: 0 }} />
          <SectionTitleInput
            hideTitle={hideTitle}
            value={section.title}
            onChange={handleTitleChange}
            placeholder="Section name..."
            inputProps={{ 'aria-label': 'Section title' }}
            onClick={e => e.stopPropagation()}
            onMouseDown={e => e.stopPropagation()}
          />
          <IconButton
            size="small"
            onClick={e => { e.stopPropagation(); setSectionMenuAnchor(e.currentTarget); }}
            aria-label="Section actions"
            aria-controls={sectionMenuAnchor ? sectionMenuId : undefined}
            aria-haspopup="true"
            sx={{ flexShrink: 0, p: 0.25 }}
          >
            <MoreHorizIcon fontSize="small" />
          </IconButton>
          <ThemedMenu
            id={sectionMenuId}
            anchorEl={sectionMenuAnchor}
            open={Boolean(sectionMenuAnchor)}
            onClose={() => setSectionMenuAnchor(null)}
            MenuListProps={{ dense: true }}
          >
            <MenuItem
              onClick={() => { setSectionMenuAnchor(null); toggleSectionHideTitle(blockId, section.id); }}
            >
              {hideTitle ? 'Show title' : 'Hide title'}
            </MenuItem>
            <MenuItem
              onClick={() => { setSectionMenuAnchor(null); removeSection(blockId, section.id); }}
              sx={{ color: 'error.main' }}
            >
              Delete section
            </MenuItem>
          </ThemedMenu>
        </SectionHeader>
      )}

      {/* ─── Inner widget rows ─── */}
      {rows.map((row, rowIdx) => {
        const rawSlotItems = computeRowSlots(widgets, row, maxCols);
        // In readOnly preview mode, skip rows that have no actual widgets placed in them.
        // This guards against any gaps between widget rows being shown as blank space.
        if (readOnly && rawSlotItems.every(item => item.bw === null)) return null;
        // Re-map so only the section-wide first empty slot gets isPrimaryEmpty = true.
        const slotItems = rawSlotItems.map(item => {
          if (item.bw !== null) return item;
          if (!sectionPrimaryAssigned) {
            sectionPrimaryAssigned = true;
            return { ...item, isPrimaryEmpty: true };
          }
          return { ...item, isPrimaryEmpty: false };
        });

        return (
          <SlotGrid key={row} maxCols={maxCols} headerHeight={sectionHeaderH} isFirst={rowIdx === 0}>
            {slotItems.map(({ slot, bw, isPrimaryEmpty }) =>
              bw ? (
                <WidgetSlotCell
                  key={`${row}-${slot}`}
                  span={bw.span}
                  readOnly={readOnly}
                  {...(!readOnly && {
                    draggable: true,
                    onDragStart: (e: React.DragEvent) => handleWidgetDragStart(e, row, slot),
                    onDragOver: (e: React.DragEvent) => handleDragOver(e, row, slot),
                    onDragLeave: () => setDragOverTarget(null),
                    onDrop: (e: React.DragEvent) => handleDrop(e, row, slot),
                  })}
                  isDragOver={!readOnly && dragOverTarget?.row === row && dragOverTarget.slot === slot}
                >
                  {!readOnly && (
                    <SlotWidgetHeader>
                      <DragIndicatorIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'grab', flexShrink: 0 }} />
                      <Typography variant="caption" fontWeight={600} noWrap sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.7rem' }}>
                        {bw.widget.config.title || bw.widget.type}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={e => { e.stopPropagation(); setWidgetMenu({ anchorEl: e.currentTarget, row, slot }); }}
                        sx={{ flexShrink: 0, p: 0.2 }}
                      >
                        <MoreHorizIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </SlotWidgetHeader>
                  )}
                  <SlotContent>
                    <SectionWidgetRenderer widgetId={bw.widget.id} config={bw.widget} width={bw.span} />
                  </SlotContent>
                  {!readOnly && (
                    <ResizeEdge
                      draggable
                      onDragStart={e => handleResizeDragStart(e, row, slot)}
                      onMouseDown={e => e.stopPropagation()}
                    />
                  )}
                </WidgetSlotCell>
              ) : readOnly ? null : (
                <SlotPlaceholder
                  key={`${row}-${slot}`}
                  onDragOver={e => handleDragOver(e, row, slot)}
                  onDragLeave={() => setDragOverTarget(null)}
                  onDrop={e => handleDrop(e, row, slot)}
                  isDragOver={dragOverTarget?.row === row && dragOverTarget.slot === slot}
                  onClick={isPrimaryEmpty ? () => setPaletteTarget({ row, slot }) : undefined}
                  isPrimary={isPrimaryEmpty}
                >
                  {isPrimaryEmpty && (
                    <>
                      <AddCircleOutlineIcon sx={{ fontSize: 20, color: 'text.secondary', mb: 0.25 }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ fontSize: '0.65rem' }}>
                        Add Widget
                      </Typography>
                    </>
                  )}
                </SlotPlaceholder>
              ),
            )}
          </SlotGrid>
        );
      })}

      {/* ─── Widget actions menu ─── */}
      {!readOnly && (
        <ThemedMenu
          id={widgetMenuId}
          anchorEl={widgetMenu?.anchorEl}
          open={Boolean(widgetMenu)}
          onClose={() => setWidgetMenu(null)}
          MenuListProps={{ dense: true }}
        >
          <MenuItem onClick={() => widgetMenu && handleConfigure(widgetMenu.row, widgetMenu.slot)}>
            Edit widget
          </MenuItem>
          {widgetMenuBw && widgetMenuBw.span < (maxCols - widgetMenuBw.slot + 1) && (
            <MenuItem
              onClick={() => {
                if (!widgetMenu || !widgetMenuBw) return;
                updateSectionWidgetSpan(blockId, section.id, widgetMenu.row, widgetMenu.slot, (widgetMenuBw.span + 1) as Slot);
                setWidgetMenu(null);
              }}
            >
              Expand →
            </MenuItem>
          )}
          {widgetMenuBw && widgetMenuBw.span > 1 && (
            <MenuItem
              onClick={() => {
                if (!widgetMenu || !widgetMenuBw) return;
                updateSectionWidgetSpan(blockId, section.id, widgetMenu.row, widgetMenu.slot, (widgetMenuBw.span - 1) as Slot);
                setWidgetMenu(null);
              }}
            >
              ← Shrink
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              if (!widgetMenu) return;
              removeWidgetFromSection(blockId, section.id, widgetMenu.row, widgetMenu.slot);
              setWidgetMenu(null);
            }}
          >
            Remove widget
          </MenuItem>
        </ThemedMenu>
      )}

      {/* ─── Widget palette ─── */}
      {!readOnly && (
        <WidgetPalette
          open={paletteTarget !== null}
          onClose={() => setPaletteTarget(null)}
          hideSections
          onSelect={type => {
            if (paletteTarget) {
              const widgetId = assignWidgetToSection(blockId, section.id, paletteTarget.row, paletteTarget.slot, type, {});
              setSelectedBlockId(blockId);
              setSelectedWidgetId(widgetId);
              setSidebarPanel('chart-config');
            }
            setPaletteTarget(null);
          }}
        />
      )}

      {/* ─── Vertical resize handle ─── */}
      {!readOnly && (
        <SectionResizeHandle
          onPointerDown={handleResizePointerDown}
          onPointerMove={handleResizePointerMove}
          onPointerUp={handleResizePointerUp}
        />
      )}
    </SectionShell>
  );
});

// ─── SectionWidgetRenderer ────────────────────────────────────────────────────

type SectionWidgetRendererProps = {
  widgetId: string;
  config: WidgetConfig;
  width: number;
};

function SectionWidgetRenderer({ widgetId, config, width }: SectionWidgetRendererProps): React.ReactElement {
  const { registry } = useWidgetRegistry();
  const data = useDashboardData(widgetId);
  const entry = registry.get(config.type);

  if (!entry) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ p: 1 }}>
        Unknown widget type: {config.type}
      </Typography>
    );
  }

  return entry.render({ widgetId, config, data, width, height: 1 });
}

// ─── Styled components ────────────────────────────────────────────────────────

const SectionShell = styled('div')(({ theme }) => ({
  // position: relative so the absolutely-positioned resize handle at the bottom
  // can overlay the last row without adding to the section height.
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  border: `1.5px solid ${theme.palette.gray?.light2 ?? theme.palette.divider}`,
  borderRadius: '10px',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.025)',
  overflow: 'hidden',
}));

/** Absolutely-positioned overlay so it doesn't consume height from inner rows. */
const SectionHeader = styled('div', {
  shouldForwardProp: prop => prop !== 'hideTitle',
})<{ hideTitle: boolean }>(({ theme, hideTitle }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 2,
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  cursor: 'grab',
  '&:active': { cursor: 'grabbing' },
  ...(hideTitle ? {
    padding: '2px 4px 2px 8px',
    height: '24px',
    // Solid background (no backdropFilter) — backdrop-filter can cause pointer-event
    // bleed beyond the element's geometric boundary, blocking the widget menus below.
    backgroundColor: theme.palette.mode === 'dark' ? `${theme.palette.primary.main}22` : `${theme.palette.primary.main}12`,
    borderBottom: `1px solid ${theme.palette.primary.light1}`,
  } : {
    padding: '10px 8px 8px 14px',
    height: '44px',
    // Opaque background so the header is visually distinct from widget rows
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.gray?.light2 ?? theme.palette.divider}`,
  }),
}));

const SectionTitleInput = styled(InputBase, {
  shouldForwardProp: prop => prop !== 'hideTitle',
})<{ hideTitle: boolean }>(({ theme, hideTitle }) => ({
  flex: 1,
  marginRight: '26px',
  ...(hideTitle ? {} : {
    // Underline beneath the title text input — matches block title style
    borderBottom: `2px solid ${theme.palette.text.disabled}`,
  }),
  '& input': {
    padding: 0,
    cursor: 'text',
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1,
    ...(hideTitle ? {
      fontSize: theme.typography.caption.fontSize,
    } : {
      fontSize: theme.typography.subtitle1.fontSize,
      fontWeight: 700,
    }),
  },
}));

const SECTION_RESIZE_H = 8;  // px — must match SectionResizeHandle height below

const SlotGrid = styled('div', {
  shouldForwardProp: prop => prop !== 'maxCols' && prop !== 'headerHeight' && prop !== 'isFirst',
})<{ maxCols: number; headerHeight: number; isFirst: boolean }>(({ theme, maxCols, headerHeight, isFirst }) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(${maxCols}, 1fr)`,
  gridTemplateRows: '1fr',
  gap: theme.spacing(0.75),
  padding: theme.spacing(0.75),
  // Push first row's content below the absolutely-positioned header overlay
  ...(isFirst && headerHeight > 0 && { paddingTop: `${headerHeight + 2}px` }),
  // Equal share of the full SectionShell height
  flex: 1,
  minHeight: 0,
  // Last row: keep content above the absolutely-positioned resize handle
  '&:last-of-type': {
    paddingBottom: `${SECTION_RESIZE_H + 2}px`,
  },
  '& + &': {
    paddingTop: 0,
    borderTop: `1px dashed ${theme.palette.divider}`,
  },
}));

const WidgetSlotCell = styled('div', {
  shouldForwardProp: prop => prop !== 'span' && prop !== 'isDragOver' && prop !== 'readOnly',
})<{ span: number; isDragOver: boolean; readOnly?: boolean }>(({ theme, span, isDragOver }) => ({
  gridColumn: `span ${span}`,
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${isDragOver ? theme.palette.primary.main : theme.palette.divider}`,
  borderRadius: '8px',
  overflow: 'hidden',
  background: theme.palette.background.paper,
  transition: 'border-color 150ms',
  position: 'relative',
}));

const SlotWidgetHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.375, 0.625),
  borderBottom: `1px solid ${theme.palette.divider}`,
  minHeight: '24px',
  flexShrink: 0,
}));

const SlotContent = styled('div')({
  flex: 1,
  overflow: 'hidden',
  position: 'relative',
});

const ResizeEdge = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  width: '8px',
  height: '100%',
  cursor: 'ew-resize',
  zIndex: 2,
  transition: 'background-color 150ms, box-shadow 150ms',
  '&:hover': {
    backgroundColor: `${theme.palette.primary.main}1A`,
    boxShadow: `inset -2px 0 0 0 ${theme.palette.primary.main}`,
  },
}));

const SlotPlaceholder = styled('div', {
  shouldForwardProp: prop => prop !== 'isDragOver' && prop !== 'isPrimary',
})<{ isDragOver: boolean; isPrimary: boolean }>(({ theme, isDragOver, isPrimary }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  border: `1.5px ${isPrimary ? 'solid' : 'dashed'} ${isDragOver ? theme.palette.primary.main : theme.palette.gray.light2}`,
  borderRadius: '8px',
  cursor: isPrimary ? 'pointer' : 'default',
  transition: 'border-color 150ms, background-color 150ms',
  backgroundColor: isDragOver ? theme.palette.primary.light4 : 'transparent',
  minHeight: 60,
  ...(isPrimary && {
    '&:hover': {
      borderColor: theme.palette.gray.light1,
      backgroundColor: theme.palette.gray.light4,
    },
  }),
}));

/**
 * Absolutely-positioned bottom strip for resizing section height.
 * Overlays the last inner row so it never adds to the section's height.
 */
const SectionResizeHandle = styled('div')(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 2,
  height: `${SECTION_RESIZE_H}px`,
  cursor: 's-resize',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background-color 150ms, box-shadow 150ms',
  '&:hover': {
    backgroundColor: `${theme.palette.primary.main}1A`,
    boxShadow: `inset 0 -3px 0 0 ${theme.palette.primary.main}`,
  },
  '&:active': {
    backgroundColor: `${theme.palette.primary.main}2A`,
  },
}));

/** Inline title displayed in preview mode — no absolute positioning, no editing chrome.
 *  Matches the builder's full-title SectionHeader: opaque background, 44 px tall, border-bottom. */
const SectionPreviewTitle = styled(Typography)(({ theme }) => ({
  flexShrink: 0,
  height: '44px',
  display: 'flex',
  alignItems: 'center',
  padding: '0 14px',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.gray?.light2 ?? theme.palette.divider}`,
}));

